import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { getISBNDataFromCache, saveISBNDataToCache } from '@/lib/supabase/isbn'
import { fetchBookDataFromAPI, type ISBNBookData, isValidISBN } from '@/services/isbn'

export interface UseISBNResult {
  data: ISBNBookData | null
  isLoading: boolean
  error: string | null
  fetchBookData: (isbn: string) => Promise<ISBNBookData | null>
  reset: () => void
}

/**
 * Hook for fetching book data by ISBN
 * First checks local cache, then falls back to Google Books API
 */
export function useISBN(): UseISBNResult {
  const [data, setData] = useState<ISBNBookData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (isbn: string): Promise<ISBNBookData | null> => {
      // Validate ISBN format
      if (!isValidISBN(isbn)) {
        throw new Error('Invalid ISBN format. Please enter a valid 10 or 13 digit ISBN.')
      }

      const cleanISBN = isbn.replace(/[- ]/g, '')

      // Step 1: Check local cache first
      const cachedData = await getISBNDataFromCache(cleanISBN)

      if (cachedData) {
        return cachedData
      }

      // Step 2: If not in cache, fetch from Google Books API
      const apiData = await fetchBookDataFromAPI(cleanISBN)

      if (!apiData) {
        throw new Error('Book not found. Please check the ISBN or enter book details manually.')
      }

      // Step 3: Save to cache for future use
      await saveISBNDataToCache(cleanISBN, apiData)

      return apiData
    },
    onSuccess: (result) => {
      setData(result)
      setError(null)
    },
    onError: (err) => {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch book data'
      setError(errorMessage)
      setData(null)
    },
  })

  const fetchBookData = async (isbn: string): Promise<ISBNBookData | null> => {
    setError(null)
    return mutation.mutateAsync(isbn)
  }

  const reset = () => {
    setData(null)
    setError(null)
    mutation.reset()
  }

  return {
    data,
    isLoading: mutation.isPending,
    error,
    fetchBookData,
    reset,
  }
}
